import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithGoogleAndEstablishSession,
  dashboardPathForRole,
  GoogleSignInError,
} from '@/lib/auth-google';
import { useAuth } from '@/auth/session';

type Props = {
  /**
   * Optional handler called after a successful sign-in. By default the
   * button navigates the user to their role's dashboard. If you need to
   * do something extra (e.g. dismiss a modal first), pass onSuccess and
   * call your own navigation inside.
   */
  onSuccess?: (user: { role: string }) => void;
  /**
   * Optional error sink. If supplied, the button delegates rendering the
   * error to the caller (e.g. existing inline error banner). If not, the
   * button shows a small inline error below itself.
   */
  onError?: (message: string) => void;
};

/**
 * "Continue with Google" button — used on /login and /register.
 *
 * - Calls signInWithGoogleAndEstablishSession() (popup → backend session)
 * - Disables itself + shows a spinner while in flight
 * - Maps typed errors to friendly inline messages
 * - On success, navigates to the role's dashboard (or calls onSuccess)
 *
 * Visual: white background, gray border, Google "G" logo. Matches the
 * design language of the existing login/register cards.
 */
export function GoogleSignInButton({ onSuccess, onError }: Props) {
  const navigate = useNavigate();
  // Pull setUser from AuthProvider so we can sync the new user into client
  // state immediately after the backend establishes the session. Without
  // this, the parent dashboard's route guard sees stale user=null and
  // bounces the user to /auth (the bug we hit pre-fix).
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleClick = async () => {
    // Reset any previous error before kicking off a new attempt.
    setLocalError(null);
    onError?.(''); // clear caller's banner too if they wired one
    setLoading(true);

    try {
      const { user } = await signInWithGoogleAndEstablishSession();

      // Push the server-provisioned user into AuthProvider state. The role
      // type from auth-google is string (server returns it as text), but
      // AuthProvider expects a discriminated union. We cast safely — the
      // backend hardcodes role=parent for new Google users and only ever
      // returns one of the known roles for linked existing users.
      setUser({
        id: String(user.id),
        email: user.email,
        role: user.role as 'admin' | 'coach' | 'parent' | 'player',
      });

      if (onSuccess) {
        onSuccess({ role: user.role });
      } else {
        navigate(dashboardPathForRole(user.role), { replace: true });
      }
    } catch (err: any) {
      // GoogleSignInError already carries a user-friendly message.
      // For anything unexpected, fall back to a generic line.
      const message =
        err instanceof GoogleSignInError
          ? err.message
          : 'Something went wrong signing in. Please try again.';

      // "cancelled_by_user" is not really an error — the user explicitly
      // closed the popup. Don't shout about it; just clear the loading
      // state silently.
      const isCancellation =
        err instanceof GoogleSignInError && err.code === 'cancelled_by_user';

      if (!isCancellation) {
        if (onError) {
          onError(message);
        } else {
          setLocalError(message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid="google-signin-button"
        aria-label="Continue with Google"
        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Spinner />
            <span>Signing in…</span>
          </>
        ) : (
          <>
            <GoogleLogo />
            <span>Continue with Google</span>
          </>
        )}
      </button>

      {/* Inline error fallback when caller didn't provide onError */}
      {localError && (
        <p
          className="text-sm text-red-600 text-center"
          role="alert"
          data-testid="google-signin-error"
        >
          {localError}
        </p>
      )}
    </div>
  );
}

/**
 * Google "G" logo, inlined as SVG so the button has no external image
 * dependency. Uses Google's official brand colors.
 */
function GoogleLogo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.616z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

/**
 * Inline spinner shown while the popup → backend round trip is in flight.
 */
function Spinner() {
  return (
    <svg
      className="animate-spin"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}